import { Box, Chip, Typography } from '@mui/material';
import { memo } from 'react';
import { bloxValues } from 'themes/constants';

export interface LabeledItemProps {
  primaryText: string;
  secondaryText?: string | null;
  chip?: { label: string; color: string } | null;
  isDataPrivate?: boolean;
  size?: 'small' | 'regular';
}

/**
 * Shared component for displaying labeled items consistently.
 * Shows primary text (main label), optional secondary text (sublabel), and optional chip.
 * Used in dropdowns, search results, and other list-based components.
 */
export const LabeledItem = memo(
  ({
    primaryText,
    secondaryText,
    chip,
    isDataPrivate,
    size = 'regular',
  }: LabeledItemProps) => {
    const fontSize =
      size === 'small' ? bloxValues.smallFontSizeRem : bloxValues.regularFontSizeRem;
    const secondaryFontSize =
      size === 'small' ? bloxValues.microFontSizeRem : bloxValues.smallFontSizeRem;

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            data-private={isDataPrivate ? true : undefined}
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '100%',
              fontSize,
            }}
          >
            {primaryText}
          </Typography>
          {secondaryText && (
            <Typography
              data-private={isDataPrivate ? true : undefined}
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '100%',
                fontSize: secondaryFontSize,
                color: (theme) => theme.palette.text.secondary,
                mt: 0.25,
              }}
            >
              {secondaryText}
            </Typography>
          )}
        </Box>
        {chip && (
          <Chip
            label={chip.label}
            color={chip.color as any}
            size="small"
            sx={{
              fontSize:
                size === 'small'
                  ? bloxValues.microFontSizeRem
                  : bloxValues.tinyFontSizeRem,
              height: size === 'small' ? '18px' : '20px',
              '& .MuiChip-label': {
                px: 1,
                fontSize:
                  size === 'small'
                    ? bloxValues.microFontSizeRem
                    : bloxValues.tinyFontSizeRem,
              },
            }}
          />
        )}
      </Box>
    );
  },
);

LabeledItem.displayName = 'LabeledItem';
